import { StateCreator } from "zustand";

import {
  IResponse,
  IRocketTokens,
  ProfileDto,
  ProfileShareSection,
  spErrorTags,
  UpdateUserDTO,
  UserOrganisation,
} from "@eventr/common";
import { setStarredAddressUser } from "@eventr/common-storybook";
import * as Sentry from "@sentry/capacitor";
import { GUEST_ORG_NAME } from "../../consts";
import { EventrGatewayAPI } from "../../data/gateway.api";
import { BoundStore, bs, useBoundStore } from "../../state/bound.store";
import { lls } from "../../state/liveLocationState/liveLocation.store";
import { CLAIMS_SETTLE_BACKOFF_MS } from "../../state/orgConfig.slice";
import { useOverlayStore } from "../../state/overlayState/overlay.store";
import { filterAndExtractIds, findMissingData } from "../account/profilePage/userProfileUtils";
import { clearStoredClaims } from "../oauth/oauth.slice";
import { useGuestStore } from "../guest/guest.store";
import { configureRevenueCat } from "../revenueCat/utils";
import { SpchatAPI } from "../../data/spchat.api";
import { chatBackend } from "../rocketchat/chatBackend";
interface ProfileState {
  profile?: ProfileDto;
  rcTokens: IRocketTokens[];
  selectedOrg: UserOrganisation | undefined;
  additionalInfo?: ProfileShareSection[];
  profileRequiredAdditionalInfo?: string[];
}

interface ProfileActions {
  fetchProfile: () => Promise<IResponse<ProfileDto>>;
  getProfile: () => Promise<ProfileDto | undefined>;
  /**
   * Fetches the user's chat credentials, one per org. `reuseWithinMs` returns the current list
   * instead when it is non-empty and was fetched that recently - with spchat every fetch mints
   * new sessions, so back-to-back fetches at start-up meant a second token and a reconnect.
   */
  getRcTokens: (options?: { reuseWithinMs?: number }) => Promise<IResponse<IRocketTokens[]>>;
  checkUserOwnership: () => boolean;
  deleteRequest: () => Promise<void>;
  setSelectedOrg: (org: UserOrganisation) => Promise<void>;
  updateRequest: (body: UpdateUserDTO) => Promise<ProfileDto>;
  /** Saves the user's hotel rewards choice for the current org. null follows the org default. */
  setHotelRewards: (hotelRewardsOnly: boolean | null) => Promise<void>;
  passwordRecoveryRequest: (email: string) => Promise<IResponse<any>>;
  setProfileShareSections: () => Promise<ProfileShareSection[]>;
  setSelectedOrgById: (orgId: string) => Promise<void>;
  getProfileShareSections: () => ProfileShareSection[];
  getProfileSharedInfo: () => ProfileShareSection[];
}

export interface ProfileSlice extends ProfileState, ProfileActions {}

// Shared "no tokens" reference, so repeated empty results keep the same array identity.
const NO_RC_TOKENS: IRocketTokens[] = [];
/** When rcTokens last came back successfully; see getRcTokens' reuseWithinMs. */
let rcTokensFetchedAt: number | undefined;

/** Saved spchat sessions are reused at start-up while every one has at least this long left. */
const SPCHAT_SESSION_REUSE_MARGIN_MS = 30 * 60 * 1000;

/**
 * spchat returns `expiresAt` with each session, and rcTokens persists across restarts, so a
 * start-up can reuse them instead of minting new sessions (and reconnecting the socket when
 * the new token replaces the saved one). Rocket.Chat gateway tokens carry no expiry and are
 * never reused this way; neither are tokens kept from the other backend.
 */
const spchatSessionsStillValid = (tokens: IRocketTokens[]): boolean =>
  chatBackend() === "spchat" &&
  tokens.every((token) => {
    const expiresAt = (token as IRocketTokens & { expiresAt?: unknown }).expiresAt;
    return typeof expiresAt === "number" && expiresAt - Date.now() > SPCHAT_SESSION_REUSE_MARGIN_MS;
  });

// In-flight token fetch, shared by concurrent callers.
let rcTokensInFlight: Promise<IResponse<IRocketTokens[]>> | null = null;

/**
 * Fetches the users, retrying until `/me` hands back a claims JWT.
 *
 * `clearStoredClaims` has just deleted the claims for the organisation being left, and
 * `/me` is the only unconditional writer of their replacement: the response-header path
 * needs a `wpUser`, which does not exist until this call succeeds. So a `/me` that answers
 * without a `claimsJWT` leaves the session with no claims at all, and every claim-gated
 * affordance off, until something else happens to refetch the users — in practice a
 * pull-to-refresh, a resume, or a relaunch.
 *
 * That answer is what a member joining an existing organisation gets, because the
 * organisation-side user is still being synced into WordPress when the app asks. WordPress
 * builds the claims from that user's roles, finds no user to read roles from, and returns
 * the profile without claims rather than failing. Hence the retry on the same ladder the
 * subscription and organisation-creation polls use.
 */
async function fetchUsersUntilClaimsLand(): Promise<void> {
  if (await bs().fetchUsers()) return;

  for (const delay of CLAIMS_SETTLE_BACKOFF_MS) {
    await new Promise((resolve) => setTimeout(resolve, delay));
    if (await bs().fetchUsers()) return;
  }

  spErrorTags("Joined an organisation whose claims never arrived", ["PROFILE", "CLAIMS"]);
}

export const createProfleSlice: StateCreator<BoundStore, [["zustand/devtools", never]], [], ProfileSlice> = (
  set,
  get,
) => ({
  profile: undefined,
  rcTokens: NO_RC_TOKENS,
  selectedOrg: undefined,
  fetchProfile: async () => {
    return EventrGatewayAPI.profile.getMe();
  },
  getProfile: async () => {
    try {
      const profile = await EventrGatewayAPI.profile.getMe();
      if (profile.status === "success" && profile.data) {
        set({ profile: profile.data });
        return profile.data;
      }
      return get().profile;
    } catch (error) {
      console.error(error);
      return get().profile;
    }
  },
  getRcTokens: async (options?: { reuseWithinMs?: number }): Promise<IResponse<IRocketTokens[]>> => {
    if (rcTokensInFlight) return rcTokensInFlight;
    const current = get().rcTokens;
    const fetchedRecently = rcTokensFetchedAt !== undefined && Date.now() - rcTokensFetchedAt < (options?.reuseWithinMs ?? 0);
    if (options?.reuseWithinMs && current.length > 0 && (fetchedRecently || spchatSessionsStillValid(current))) {
      return { status: "success", data: current } as IResponse<IRocketTokens[]>;
    }

    const fetch = async () => {
      const response =
        chatBackend() === "spchat"
          ? await SpchatAPI.createSessions()
          : await EventrGatewayAPI.rocketchat.getRocketchatToken();
      // A failed fetch keeps the tokens already held. A refused credential is dropped where the
      // chat login fails.
      if (response.status === "success") {
        set({ rcTokens: response.data ?? NO_RC_TOKENS });
        rcTokensFetchedAt = Date.now();
      } else {
        rcTokensFetchedAt = undefined;
      }
      return response;
    };

    rcTokensInFlight = fetch().finally(() => {
      rcTokensInFlight = null;
    });
    return rcTokensInFlight;
  },
  checkUserOwnership: () => {
    const { profile } = get();
    if (!profile) return false;
    const isOwner = profile.organisations.some((org) => {
      return (
        org.roles &&
        org.roles.some((role) => {
          return role === "owner";
        })
      );
    });
    return isOwner;
  },
  deleteRequest: async () => {
    const resp = await EventrGatewayAPI.profile.delete();
    if (resp.status === "success") {
      bs().logout();
    } else {
      spErrorTags(resp.message, ["PROFILE"], resp);
    }
  },
  setSelectedOrg: async (org) => {
    console.log("setSelectedOrg", org);
    if (org) {
      if (useGuestStore.getState().isGuestMode) {
        set({
          selectedOrg: { name: GUEST_ORG_NAME },
          orgConfig: {},
          wpUser: undefined,
        });
        return;
      }
      clearStoredClaims();
      set({ selectedOrg: org });
      // dont make requests if org is being created
      if (org.status === "creating") return;
      set({ itineraryData: {} });
      await fetchUsersUntilClaimsLand();
      // Without a WordPress user forceItineraryMapUpdate below has nothing to fetch with. Settle the
      // map anyway, as a failed fetch does, so Home shows its empty state rather than nothing.
      if (!get().wpUser) set({ itineraryMapSettled: true });
      // make all org request
      const promises = [];
      promises.push(
        get().fetchOrgConfig(),
        get().fetchFields(),
        get().fetchSections(),
        get().forceItineraryMapUpdate(),
        get().loadRequests(),
        get().fetchAllLists(),
        lls().stopSharingLiveLocation(),
        lls().init("org-changed"),
      );
      await Promise.all(promises);
      // resolve itinerary to be selected
      await get().resolveItinId();
    } else {
      set({ selectedOrg: org, wpUser: undefined, orgConfig: {} });
    }
  },
  setSelectedOrgById: async (orgId: string) => {
    useOverlayStore.getState().setLoading(true);
    const org = get().profile?.organisations?.find((org) => org.name === orgId);
    if (org) {
      await get().setSelectedOrg(org);
    } else {
      const newProfile = await get().getProfile();
      const newOrg = newProfile?.organisations?.find((org) => org.name === orgId);
      if (newOrg) await get().setSelectedOrg(newOrg);
      spErrorTags("Org not found in user profile " + orgId);
    }
    useOverlayStore.getState().setLoading(false);
  },
  updateRequest: async (body) => {
    const resp = await EventrGatewayAPI.profile.update(body);
    if (resp.status === "success" && resp.data) {
      set({
        profile: {
          ...resp.data,
        },
        selectedOrg: resp.data.organisations?.find((s) => s.name === bs().selectedOrg?.name),
      });
      return resp.data;
    } else {
      spErrorTags(resp.message, ["PROFILE"], resp);
      throw new Error("Failed to update profile");
    }
  },
  setHotelRewards: async (hotelRewardsOnly) => {
    const resp = await EventrGatewayAPI.profile.setHotelRewards(hotelRewardsOnly);
    if (resp.status === "success" && resp.data) {
      set({
        profile: resp.data,
        selectedOrg: resp.data.organisations?.find((s) => s.name === bs().selectedOrg?.name),
      });
    } else {
      spErrorTags(resp.message, ["PROFILE", "HOTEL"], resp);
      throw new Error("Failed to save hotel rewards setting");
    }
  },
  passwordRecoveryRequest: async (email: string) => {
    const resp = await EventrGatewayAPI.profile.forgotPassword(email.toLowerCase());
    return resp;
  },
  setProfileShareSections: async (): Promise<ProfileShareSection[]> => {
    try {
      const resp = await EventrGatewayAPI.profile.profileShare.fetchAdditionalInfoFields();
      if (resp.status === "success") {
        const additionalInfo = resp.data ?? [];

        // Set additionalInfo first
        set({
          additionalInfo,
        });

        // Now calculate profileRequired using the fetched data
        const requiredSections = filterAndExtractIds(
          additionalInfo.filter((s: ProfileShareSection) => s.required && s.enabled),
        );

        set({
          profileRequiredAdditionalInfo:
            findMissingData(requiredSections, get().selectedOrg?.sharedProperties ?? []) ?? [],
        });

        return additionalInfo;
      } else {
        throw new Error("Could not fetch additonal info data");
      }
    } catch (error: any) {
      if (error?.code === "ERR_CANCELED") return [];
      spErrorTags("Could not fetch additonal info data", ["PROFILE"], error);
      return [];
    }
  },
  getProfileShareSections: (): ProfileShareSection[] => {
    const sections = (get()?.additionalInfo as ProfileShareSection[]) ?? [];
    return sections;
  },
  getProfileSharedInfo: (): ProfileShareSection[] => {
    return (get().profile?.additionalInfo as ProfileShareSection[]) ?? [];
  },
});

// subscriptions to the store

/**
 * Starts this module's store subscriptions. `startStoreSubscriptions` (state/store-subscriptions.ts)
 * calls it once, from the app entry, after the whole store graph has loaded.
 */
export function startProfileSubscriptions() {
  // gwApiClient adds the token in an interceptor, so address fields cannot read the user from it.
  useBoundStore.subscribe((state) => state.profile?._id, setStarredAddressUser, { fireImmediately: true });

  useBoundStore.subscribe(
    (state) => state.profile,
    (profile, prev) => {
      if (profile) {
        //set user context for sentry
        Sentry.setUser({ email: profile?.email, id: profile?._id });
        if (bs().loginCompleted) {
          //deal with when user is removed from org

          const selectedOrg = bs().selectedOrg;
          const org = profile.organisations?.find((org) => org.name === selectedOrg?.name);
          if (org === undefined && selectedOrg !== undefined) {
            console.log("selected org not found, setting to first org");
            const firstOrg = getReadyOrgs(profile)[0];
            if (firstOrg !== undefined) {
              bs().setSelectedOrg(firstOrg);
            }
          }

          if (prev && !bs().selectedOrg) {
            console.log("bs().selectedOrg is undefined, setting to first org");

            const firstOrg = getReadyOrgs(profile)[0];
            if (firstOrg !== undefined) {
              bs().setSelectedOrg(firstOrg);
            }
          }
        }

        // Imported here rather than at module scope: checkTermsAndCond renders
        // RequiredActionDialog, which pulls BriefcaseBobbin, and this slice is part of the
        // bound store - so a static import puts ~52KB gzipped of signed-in-only dialog in
        // the entry chunk of every page, the public guest itinerary included. The call was
        // already fire-and-forget, so nothing here waits on it.
        void import("./checkTermsAndCond")
          .then((m) => m.checkForTermsAndConditions(profile))
          .catch((error) => spErrorTags(`Terms check failed: ${error}`, ["PROFILE"]));
        // if no ready orgs, wait 10 seconds and try again
        if (getReadyOrgs(profile).length === 0) {
          setTimeout(async () => {
            await bs().getProfile();
          }, 5000);
        }
        // rerun login request if selected orgs states changed from creating to active
        selectedOrgStatusChanged(profile, prev);
      }
    },
    {
      fireImmediately: true,
    },
  );
  useBoundStore.subscribe(
    (state) => state.selectedOrg,
    (org) => {
      if (!org) return;
      configureRevenueCat(org.name);
    },
    {
      fireImmediately: true,
    },
  );
}

//Use this to get the orgs that are not in the creating state as we dont want to show them
const getReadyOrgs = (profile: ProfileDto) => {
  return (profile?.organisations ?? []).filter((org) => org.status !== "creating") ?? [];
};

/**
 * Reruns onLogin when the selected org flips from creating to active, then
 * polls fetchOrgConfig with backoff until permission claims update.
 * @param currentProfile the current profile
 * @param previousProfile the previous profile
 */
const selectedOrgStatusChanged = async (currentProfile?: ProfileDto, previousProfile?: ProfileDto) => {
  if (!currentProfile || !previousProfile) return;
  const currentOrg = bs().selectedOrg;
  const currentProfileOrg = currentProfile?.organisations?.find((org) => org.name === currentOrg?.name);
  const previousProfileOrg = previousProfile?.organisations?.find((org) => org.name === currentOrg?.name);

  if (currentProfileOrg?.status === "active" && previousProfileOrg?.status === "creating") {
    const claimsAtStart = useBoundStore.getState().claimsVersion;
    const claimsChanged = () => useBoundStore.getState().claimsVersion !== claimsAtStart;

    await bs().onLogin();
    if (claimsChanged()) return;

    for (const delay of CLAIMS_SETTLE_BACKOFF_MS) {
      await new Promise((resolve) => setTimeout(resolve, delay));
      await bs().fetchOrgConfig();
      if (claimsChanged()) return;
    }
  }
};
